import React, { useState, useEffect } from "react"
import Typography from "aldoo-ra/Typography"
import DataTable from "aldoo-ra/DataTable"
import Button from "aldoo-ra/Button"
import NewsletterAPI from "../api"
import Badge from "aldoo-ra/Badge"
import ReleaseGenerator from "../ReleaseGenerator"
import ReleaseDetails from "../ReleaseDetails"
import Spinner from "aldoo-ra/Spinner"
import PubSub from "pubsub-js"
import NewsletterRealtimeController from "../realtime-controller"
import PermissionCheck, { usePermissionCheck } from "aldoo-ra/PermissionChecker"
import { PlusCircleIcon } from "@heroicons/react/24/outline"
import { showPromptDialogAsync } from "aldoo-ra/PromptDialog"
import { showNotification } from "aldoo-ra/Notification"

const NewsletterReleases = React.memo(() => {
  const componentId = React.useRef(Math.random().toString(36).substring(7))
  const handlePermissionError = usePermissionCheck('role:newsletter')
  
  const [loading, setLoading] = useState(false)
  const [releases, setReleases] = useState([])
  const [pagination, setPagination] = useState({
    page: 1,
    itemsPerPage: 25,
    totalItems: 0,
    totalPages: 0
  })
  const [showReleaseGenerator, setShowReleaseGenerator] = useState(false)
  const [showReleaseDetails, setShowReleaseDetails] = useState(false)
  const [selectedReleaseId, setSelectedReleaseId] = useState(null)

  const loadReleases = async (page = 1, filters = {}) => {
    try {
      setLoading(true)
      
      const response = await NewsletterAPI.getReleases({
        page,
        itemsPerPage: pagination.itemsPerPage,
        filters
      })
      
      // Check for permission errors first
      if (handlePermissionError && handlePermissionError(response)) {
        return
      }
      
      if (response && response.success) {
        setReleases(Array.isArray(response.releases) ? response.releases : [])
        setPagination(prev => ({
          ...prev,
          page: response.pagination?.page || page,
          totalItems: response.pagination?.totalItems || 0,
          totalPages: response.pagination?.totalPages || 0
        }))
      } else {
        console.error('Invalid API response:', response)
        setReleases([])
      }
      
    } catch (error) {
      console.error('Failed to load releases:', error)
      setReleases([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadReleases()

    // Check if we should open the dialog
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search)
      if (params.get('openDialog') === 'true') {
        setShowReleaseGenerator(true)
        // Clean up URL
        const url = new URL(window.location)
        url.searchParams.delete('openDialog')
        window.history.replaceState({}, '', url)
      }
    }
  }, [])

  // Force re-render trigger
  const [, forceUpdate] = React.useReducer(x => x + 1, 0)

  // Memoize event handlers to prevent useEffect re-runs
  const handleReleaseStatusUpdate = React.useCallback((data) => {
    setReleases(prevReleases => {
      const foundRelease = prevReleases.find(r => r._id === data.releaseId)
      if (!foundRelease) {
        return prevReleases
      }

      const updatedReleases = prevReleases.map(release => {
        if (release._id !== data.releaseId) return release

        const updated = {
          ...release,
          status: data.status,
          startedAt: data.startedAt || release.startedAt,
          resumedAt: data.resumedAt,
          updatedAt: data.updatedAt,
          completedAt: data.completedAt,
          emailsSent: data.emailsSent !== undefined ? data.emailsSent : release.emailsSent,
          emailsFailed: data.emailsFailed !== undefined ? data.emailsFailed : release.emailsFailed,
          progress: data.progress !== undefined ? data.progress : release.progress,
          audienceSize: data.audienceSize !== undefined ? data.audienceSize : release.audienceSize,
          _elapsedSeconds: data._elapsedSeconds !== undefined ? data._elapsedSeconds : release._elapsedSeconds,
          _lastUpdated: Date.now()
        }

        return updated
      })

      return updatedReleases
    })
  }, [])

  const handleReleaseProgress = React.useCallback((data) => {
    setReleases(prevReleases => {
      const updatedReleases = prevReleases.map(release => 
        release._id === data.releaseId 
          ? { 
              ...release, 
              status: data.status,
              emailsSent: data.sent,
              emailsFailed: data.failed,
              progress: data.progress,
              _lastUpdated: Date.now()
            }
          : release
      )
      
      return updatedReleases
    })
  }, [])

  const handleReleaseCompletion = React.useCallback((data) => {
    setReleases(prevReleases => {
      const updatedReleases = prevReleases.map(release => {
        if (release._id === data.releaseId) {
          return { 
            ...release, 
            status: 'completed',
            emailsSent: data.totalSent,
            emailsFailed: data.totalFailed,
            completedAt: new Date(),
            _lastUpdated: Date.now()
          }
        }
        return release
      })
      
      return updatedReleases
    })
  }, [])

  const handleReleaseDeletion = React.useCallback((data) => {
    setReleases(prevReleases => 
      prevReleases.filter(release => release._id !== data.releaseId)
    )
  }, [])

  // Real-time subscription for release status updates via PubSub
  useEffect(() => {
    // Initialize realtime controller (idempotent)
    NewsletterRealtimeController.init()

    // Subscribe to PubSub events published by the centralized controller
    const tokens = [
      PubSub.subscribe('newsletter:release_status_updated', (msg, data) => handleReleaseStatusUpdate(data)),
      PubSub.subscribe('newsletter:release_progress', (msg, data) => handleReleaseProgress(data)),
      PubSub.subscribe('newsletter:release_completed', (msg, data) => handleReleaseCompletion(data)),
      PubSub.subscribe('newsletter:release_deleted', (msg, data) => handleReleaseDeletion(data))
    ]

    // Cleanup
    return () => {
      tokens.forEach(token => PubSub.unsubscribe(token))
    }
  }, [handleReleaseStatusUpdate, handleReleaseProgress, handleReleaseCompletion, handleReleaseDeletion])

  // Poll stats for running releases
  useEffect(() => {
    const runningReleases = releases.filter(r => r.status === 'running')
    if (runningReleases.length === 0) return

    const socket = NewsletterRealtimeController.socket
    if (!socket || !socket.connected) return

    const pollStats = () => {
      runningReleases.forEach(release => {
        socket.emit('newsletter:get_release_stats', { release_id: release._id }, (response) => {
          if (response?.success) {
            // Unwrap the data if it's nested (realtime system wraps responses)
            const statsData = response.data || response

            setReleases(prevReleases => {
              return prevReleases.map(r => {
                if (r._id !== release._id) return r

                const emailsSent = statsData.emailsSent !== undefined ? statsData.emailsSent : r.emailsSent
                const audienceSize = statsData.audienceSize || r.audienceSize
                const progress = audienceSize > 0 ? Math.min(100, Math.round((emailsSent / audienceSize) * 100)) : 0

                return {
                  ...r,
                  deliveryStats: statsData.deliveryStats || r.deliveryStats,
                  templateStats: statsData.templateStats || r.templateStats,
                  emailsSent: emailsSent,
                  audienceSize: audienceSize,
                  progress: progress,
                  _lastUpdated: Date.now()
                }
              })
            })
          }
        })
      })
    }

    // Poll immediately
    pollStats()

    // Then poll every 5 seconds
    const pollInterval = setInterval(pollStats, 5000)

    return () => clearInterval(pollInterval)
  }, [releases.map(r => `${r._id}:${r.status}`).join(',')])

  const handlePageChange = (newPage) => {
    loadReleases(newPage)
  }

  const getStatusBadge = (status) => {
    const variants = {
      'scheduled': 'default',      // Gray - not started yet
      'running': 'info',           // Blue - actively running
      'completed': 'success',      // Green - successfully finished
      'paused': 'warning',         // Yellow/Orange - temporarily stopped
      'failed': 'error',           // Red - error occurred
      'cancelled': 'admin_error'   // Dark red - permanently stopped
    }
    return <Badge variant={variants[status] || 'default'}>{status}</Badge>
  }

  const handleCreateRelease = () => {
    setShowReleaseGenerator(true)
  }

  const handleReleaseCreated = (newRelease) => {
    // Refresh the releases list
    loadReleases(pagination.page)
  }

  const handleViewRelease = (release) => {
    setSelectedReleaseId(release._id)
    setShowReleaseDetails(true)
  }

  const handleBulkDelete = async (selectedIds, selectedData) => {
    const confirmed = await showPromptDialogAsync({
      variant: "admin",
      title: 'Delete Multiple Releases?',
      message: `Are you sure you want to delete ${selectedIds.length} release${selectedIds.length !== 1 ? 's' : ''}? This action cannot be undone.`,
      confirmText: 'Delete',
      cancelText: 'Cancel',
      type: 'danger'
    })

    if (!confirmed) return

    try {
      const deletePromises = selectedIds.map(releaseId =>
        NewsletterAPI.deleteRelease({ releaseId })
      )

      const results = await Promise.all(deletePromises)

      const successCount = results.filter(r => r.success).length
      const failCount = results.length - successCount

      if (successCount > 0) {
        showNotification({
          message: `Successfully deleted ${successCount} release${successCount !== 1 ? 's' : ''}`,
          className: "bg-success text-text_inv",
        })
      }

      if (failCount > 0) {
        showNotification({
          message: `Failed to delete ${failCount} release${failCount !== 1 ? 's' : ''}`,
          className: "bg-error text-text_inv",
        })
      }

      loadReleases(pagination.page)
    } catch (error) {
      console.error('Bulk delete error:', error)
      showNotification({
        message: 'Failed to delete releases',
        className: "bg-error text-text_inv",
      })
    }
  }

  const columns = [
    {
      id: 'name',
      title: 'Release Name',
      sortable: true,
      headerClassName: "w-[30%]",
      render: (value, row) => (
        <button
          className="text-left hover:text-admin_primary hover:underline cursor-pointer transition-colors duration-200"
          onClick={() => handleViewRelease(row)}
        >
          {value}
        </button>
      )
    },
    {
      id: 'status',
      title: 'Status',
      headerClassName: "w-[100px]",
      width: '100px',
      render: (value) => getStatusBadge(value)
    },
    {
      id: 'emailsSent',
      title: 'Sent',
      headerClassName: "w-[100px]",
      render: (value, row) => {
        const sent = value || 0;
        const total = row.audienceSize || 0;
        if (total === 0) return (
          <div className="w-12 h-12 flex items-center justify-center">
            <span className="text-xs font-semibold text-admin_text">0%</span>
          </div>
        );
        const percentage = Math.min(100, Math.round((sent / total) * 100));

        // Show real-time progress for running releases
        const displayPercentage = row.status === 'running' && row.progress !== undefined
          ? Math.min(100, row.progress)
          : percentage;

        // Consistent container size to prevent jumping
        return (
          <div className="w-12 h-12 flex items-center justify-center relative">
            {/* Spinner with fade transition */}
            <div className={`absolute inset-0 transition-opacity duration-300 ${
              row.status === 'running' ? 'opacity-100' : 'opacity-0'
            }`}>
              <Spinner variant="loading" size="xl" />
            </div>

            {/* Percentage text - always centered */}
            <span className="relative z-10 text-xs font-semibold text-admin_text">
              {displayPercentage}%
            </span>
          </div>
        );
      }
    },
    {
      id: 'audienceSize',
      title: 'Audience',
      sortable: true,
      headerClassName: "w-[100px]",
      render: (value) => (value || 0).toLocaleString()
    },
    {
      id: 'scheduledAtFormatted',
      title: 'Scheduled Time',
      sortable: true,
      headerClassName: "w-[200px]",
    },
    {
      id: 'createdAtFormatted',
      title: 'Created',
      sortable: true,
      headerClassName: "w-[150px]",
    }
  ]

  return (
    <div className="p-6">
      <PermissionCheck permission="role:newsletter" />
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <Typography 
            component="h1"
            variant="admin_h3"
          >
            Newsletter Releases
          </Typography>

          <Button 
            variant="admin_primary" 
            onClick={handleCreateRelease} 
          >
            <PlusCircleIcon className="w-5 h-5 mr-1" />
            <span>Create Release</span>
          </Button>
        </div>


        <DataTable
          variant="admin_browser"
          data={releases || []}
          localUserPreferencesKey="admin.newsletter.releases"
          renders={columns}
          loading={loading}
          enableSelection={true}
          itemIdField="_id"
          bulkActions={[
            {
              label: 'Delete Selected',
              variant: 'admin_error',
              onClick: handleBulkDelete
            }
          ]}
          key={`datatable-${releases.length}-${releases.map(r => `${r._id}-${r.status}-${r._lastUpdated || 0}`).join(',').slice(0, 50)}`}
          pagination={{
            ...pagination,
            onPageChange: handlePageChange
          }}
          searchField="name description"
          sort={{ createdAt: -1 }}
          styleOverrides={{
            table: "w-full table-fixed",
            container: "relative w-full flex flex-col h-[85vh] justify-start bg-admin_paper rounded-3xl px-6 pt-12 pb-6",
            searchInput: "absolute top-4 right-4 text-text bg-transparent outline-none flex-1 w-[350px] h-[30px] px-2 py-1",
          }}
        />
      </div>

      {/* Release Generator Modal */}
      <ReleaseGenerator
        isOpen={showReleaseGenerator}
        onClose={() => setShowReleaseGenerator(false)}
        onSuccess={handleReleaseCreated}
      />

      {/* Release Details Modal */}
      <ReleaseDetails
        isOpen={showReleaseDetails}
        onClose={() => {
          setShowReleaseDetails(false)
          setSelectedReleaseId(null)
        }}
        releaseId={selectedReleaseId}
        onReleaseUpdated={() => loadReleases(pagination.page)}
      />
    </div>
  )
})

export default NewsletterReleases